import { Input, Select } from "antd";
import { forwardRef, useImperativeHandle } from "react";
import { Controller } from "react-hook-form";
import txtIcon from "../../../images/file-txt-icon.png";
import LabelRequired from "../../Common/LabelRequired";
import { useExtensionBoxForm } from "./hooks-form";

interface ExtensionBoxDownloadProps {
  serialNumber: string;
  hwNumber: string;
  notificationEmail: string;
  onNotificationEmailChange: (value: string) => void;
  agentName: string;
  onAgentNameChange: (value: string) => void;
  agencyContactPerson: string;
  onAgencyContactPersonChange: (value: string) => void;
  agencyEmail: string;
  onAgencyEmailChange: (value: string) => void;
  clinicName: string;
  setClinicName: (value: string) => void;
  userContactName: string;
  onUserContactNameChange: (value: string) => void;
  userContactEmail: string;
  onUserContactEmailChange: (value: string) => void;
  fileName: string;
}

export interface ExtensionBoxDownloadRef {
  validate: () => Promise<boolean>;
}

const ExtensionBoxDownload = forwardRef<
  ExtensionBoxDownloadRef,
  ExtensionBoxDownloadProps
>(
  (
    {
      serialNumber,
      hwNumber,
      notificationEmail,
      onNotificationEmailChange,
      agentName,
      onAgentNameChange,
      agencyContactPerson,
      onAgencyContactPersonChange,
      agencyEmail,
      onAgencyEmailChange,
      clinicName,
      setClinicName,
      userContactName,
      onUserContactNameChange,
      userContactEmail,
      onUserContactEmailChange,
      fileName,
    },
    ref,
  ) => {
    const { control, errors, emailOptions, handleEmailBlur, trigger } =
      useExtensionBoxForm({
        defaultValues: {
          notificationEmail,
          agentName,
          agencyContactPerson,
          agencyEmail,
          clinicName,
          userContactName,
          userContactEmail,
        },
      });

    useImperativeHandle(ref, () => ({
      validate: async () => {
        return await trigger();
      },
    }));
    return (
      <div className="p-6 bg-[#F4F6F9] border border-[#BCC2FF] rounded-2xl">
        <div className="flex items-center mb-4">
          <span className="text-[#2C3A4B] text-sm font-medium">{fileName}</span>
          <img className="h-6" src={txtIcon} alt="txtIcon" />
        </div>
        <div className="grid grid-cols-2 gap-6">
          {/* <div className="w-1/2 flex flex-col space-y-4"> */}
          <div>
            <LabelRequired text="Camera serial number" />
            <Input
              type="text"
              className="w-full h-[46px]"
              disabled
              value={serialNumber}
            />
          </div>
          <div>
            <LabelRequired text="End User (Clinic) Name" required />
            <Controller
              name="clinicName"
              control={control}
              render={({ field }) => (
                <div>
                  <Input
                    {...field}
                    type="text"
                    className="w-full h-[46px]"
                    status={errors.clinicName ? "error" : ""}
                    placeholder="Enter end user (clinic) name"
                    value={field.value || ""}
                    maxLength={255}
                    onChange={(e) => {
                      field.onChange(e);
                      setClinicName(e.target.value);
                    }}
                    onBlur={(e) => {
                      const trimmed = e.target.value.trim();
                      field.onChange(trimmed);
                      setClinicName(trimmed);
                    }}
                  />
                  {errors.clinicName && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.clinicName.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <div>
            <LabelRequired text="HW serial number" />
            <Input
              type="text"
              className="w-full h-[46px]"
              disabled
              value={hwNumber}
            />
          </div>
          <div>
            <LabelRequired text="End user contact name" required={false} />
            <Controller
              name="userContactName"
              control={control}
              render={({ field }) => (
                <div className={errors.agentName ? "mb-[38px]" : ""}>
                  <Input
                    {...field}
                    type="text"
                    className="w-full h-[46px]"
                    status={errors.userContactName ? "error" : ""}
                    placeholder="Enter end user contact name"
                    value={field.value || ""}
                    maxLength={255}
                    onChange={(e) => {
                      field.onChange(e);
                      onUserContactNameChange(e.target.value);
                    }}
                    onBlur={(e) => {
                      const trimmed = e.target.value.trim();
                      field.onChange(trimmed);
                      onUserContactNameChange(trimmed);
                    }}
                  />
                  {errors.userContactName && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.userContactName.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <div>
            <LabelRequired text="Agent name" />
            <Input
              type="text"
              className="w-full h-[46px]"
              disabled
              value={agentName}
            />
          </div>
          <div>
            <LabelRequired
              required={false}
              text="End user contact email address"
            />
            <Controller
              name="userContactEmail"
              control={control}
              render={({ field }) => (
                <div className={errors.agencyContactPerson ? "mb-[38px]" : ""}>
                  <Input
                    {...field}
                    type="email"
                    placeholder="Enter email address"
                    className="w-full h-[46px]"
                    maxLength={255}
                    onBlur={(e) => {
                      field.onBlur();
                      handleEmailBlur();
                    }}
                    status={errors.userContactEmail ? "error" : ""}
                    value={field.value || ""}
                    onChange={(e) => {
                      const trimmedValue = e.target.value.trim();
                      field.onChange(trimmedValue);
                      onUserContactEmailChange(trimmedValue);
                    }}
                  />
                  {errors.userContactEmail && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.userContactEmail.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <div>
            <LabelRequired text="Name of person in charge of the agency" />
            <Input
              type="text"
              className="w-full h-[46px]"
              disabled
              value={agencyContactPerson}
            />
          </div>
          <div>
            <LabelRequired
              text="Email address to send download information"
              required
            />
            <Controller
              name="notificationEmail"
              control={control}
              render={({ field }) => (
                <div>
                  <Select
                    {...field}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      onNotificationEmailChange(value as string);
                    }}
                    className="!w-full !h-[46px]"
                    options={emailOptions}
                    notFoundContent={
                      <div className="p-2 text-center text-gray-500">
                        No Data
                      </div>
                    }
                    status={errors.notificationEmail ? "error" : ""}
                  />
                  {errors.notificationEmail && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.notificationEmail.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <div>
            <LabelRequired text="Agency contact email address" />
            <Input
              type="text"
              className="w-full h-[46px]"
              disabled
              value={agencyEmail}
            />
          </div>
          {/* </div> */}
          {/* <div className="flex flex-col space-y-4 w-1/2"> */}

          {/* </div> */}
        </div>
      </div>
    );
  },
);

export default ExtensionBoxDownload;
